// Step 3 — Connect LinkedIn account (US-020, US-021, US-022).
//
// Pixel-faithful to `prototypes_and_docs/ls-onboarding-v2.jsx:249-401`.
//
// The full DS-D `ConnectAccountFlow` composite is also available for the
// dedicated Settings → Connect screen (Wave 7). For onboarding we render
// a slimmer 2-tab variant inline because the wizard layout caps width at
// 528px — the full composite expects 720px and ships its own Header/back
// chrome that would conflict with the OnboardingStep eyebrow + footer.
// `// DS-GAP:` not raised — composite is appropriate elsewhere.

import { ArrowRight, ChevronLeft, Copy, ExternalLink, Lock, Puzzle, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ApiError,
  useCreateLinkedinAccount,
  useDisconnectExtension,
  useExtensionStatus,
  usePairExtension,
  useUserHubChannel,
  useValidateCookie,
  type ExtensionStatusResponse,
  type PairExtensionResponse,
  type UserHubServerEvent,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Avatar,
  Banner,
  Button,
  Card,
  Field,
  Input,
  OnboardingStep,
  Pill,
  Spinner,
} from '@barel/ui';

import {
  BAREL_EXTENSION_ID,
  BAREL_EXTENSION_INSTALL_URL,
  HAS_PUBLISHED_EXTENSION,
} from '@/lib/extensionConfig';

import { useExtensionPresence } from '../lib/useExtensionPresence';
import type { ConnectMethod } from '../lib/schemas';

interface ConnectProps {
  method: ConnectMethod;
  connected: boolean;
  onMethodChange: (method: ConnectMethod) => void;
  onConnected: () => void;
  onDisconnected?: () => void;
  onBack: () => void;
  onContinue: () => void;
}

interface ConnectedAccountDisplay {
  name: string;
  vanity: string;
  avatarUrl?: string | null;
}

const COOKIE_MIN_LENGTH = 10;

export function Connect({
  method,
  connected,
  onMethodChange,
  onConnected,
  onDisconnected,
  onBack,
  onContinue,
}: ConnectProps) {
  const t = useT('onboarding');
  const tCommon = useT('common');
  const presence = useExtensionPresence(BAREL_EXTENSION_ID);
  const [cookie, setCookie] = useState<string>('');
  const [cookieError, setCookieError] = useState<string | null>(null);
  const [extensionConnected, setExtensionConnected] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState<ConnectedAccountDisplay | null>(null);
  const validateCookie = useValidateCookie();
  const createLinkedinAccount = useCreateLinkedinAccount();
  const connecting = validateCookie.isPending || createLinkedinAccount.isPending;
  const [autoGeneratePairingCode, setAutoGeneratePairingCode] = useState(false);
  const connectionAckedRef = useRef(connected);
  const isConnected = connected || extensionConnected;
  const connectedStatus = useExtensionStatus({
    enabled: isConnected,
    refetchInterval: isConnected ? 2000 : false,
    retry: false,
  });
  const statusAccount = useMemo(
    () => getConnectedAccountFromStatus(connectedStatus.data),
    [connectedStatus.data],
  );
  const displayedAccount = connectedAccount ?? statusAccount;
  const disconnectExtension = useDisconnectExtension();

  // Keep the ack flag in sync with the parent-owned `connected` prop without
  // mutating the ref during render (React correctness). Once the parent
  // considers us connected, `markConnected` must not re-fire `onConnected`.
  useEffect(() => {
    if (connected) connectionAckedRef.current = true;
  }, [connected]);

  const markConnected = useCallback(
    (account?: ConnectedAccountDisplay) => {
      if (account) setConnectedAccount(account);
      if (connectionAckedRef.current) return;
      connectionAckedRef.current = true;
      setExtensionConnected(true);
      onConnected();
    },
    [onConnected],
  );

  const handleDisconnect = useCallback(async () => {
    try {
      await disconnectExtension.mutateAsync();
    } catch {
      // React Query owns the error state rendered next to the button.
      return;
    }
    connectionAckedRef.current = false;
    setExtensionConnected(false);
    setConnectedAccount(null);
    setAutoGeneratePairingCode(true);
    onDisconnected?.();
  }, [disconnectExtension, onDisconnected]);

  useEffect(() => {
    if (!statusAccount) return;
    setConnectedAccount(statusAccount);
  }, [statusAccount]);

  const tryCookieConnect = async (): Promise<void> => {
    setCookieError(null);
    const liAt = cookie.trim();
    if (liAt.length < COOKIE_MIN_LENGTH) {
      setCookieError(t('step3.cookie.tooShort'));
      return;
    }
    try {
      // 1. Validate the cookie with /voyager-api/me — surfaces a real
      //    profile so we can persist a meaningful account row.
      const validated = await validateCookie.mutateAsync({ liAt });
      // 2. Persist the LinkedIn account.
      const created = await createLinkedinAccount.mutateAsync({
        name: validated.profile.name,
        vanitySlug: validated.profile.vanitySlug ?? undefined,
        linkedinUrn: validated.profile.linkedinUrn ?? undefined,
        avatarUrl: validated.profile.avatarUrl ?? undefined,
        liAt,
        source: 'cookie',
      });
      markConnected({
        name: created.account.name,
        vanity: created.account.profileUrl ?? '',
        avatarUrl: created.account.avatarUrl,
      });
    } catch (err) {
      // Map known API error codes to localised copy; default to the generic
      // validation banner so the wizard remains usable.
      if (err instanceof ApiError) {
        if (err.code === 'account.cookie_invalid') {
          setCookieError(t('step3.cookie.invalid'));
          return;
        }
        if (err.code === 'validation.failed') {
          setCookieError(t('step3.cookie.invalid'));
          return;
        }
      }
      setCookieError(t('step3.cookie.serverError'));
    }
  };

  return (
    <OnboardingStep
      width="sm"
      eyebrow={t('step3.eyebrow')}
      title={t('step3.title')}
      description={t('step3.subtitle')}
      primary={
        isConnected ? (
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={onContinue}
            iconRight={<ArrowRight aria-hidden style={{ width: 14, height: 14 }} />}
            data-testid="onboarding-step3-continue"
          >
            {t('wizard.continue')}
          </Button>
        ) : null
      }
      secondary={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          iconLeft={<ChevronLeft aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step3-back"
        >
          {t('wizard.back')}
        </Button>
      }
    >
      {isConnected ? (
        <DetectedAccountCard
          method={method}
          account={displayedAccount}
          onDisconnect={handleDisconnect}
          disconnectPending={disconnectExtension.isPending}
          disconnectError={
            disconnectExtension.error
              ? disconnectExtension.error instanceof ApiError
                ? disconnectExtension.error.message
                : t('step3.connected.disconnectError')
              : null
          }
        />
      ) : (
        <>
          <MethodTabs
            method={method}
            onChange={onMethodChange}
            extensionLabel={t('step3.method.extension')}
            cookieLabel={t('step3.method.cookie')}
            recommendedLabel={t('step3.method.recommended')}
            comingSoonLabel={tCommon('states.comingSoon')}
          />
          {method === 'extension' ? (
            <ExtensionPanel
              presence={presence}
              onConnected={markConnected}
              autoGenerate={autoGeneratePairingCode}
              onAutoGenerated={() => setAutoGeneratePairingCode(false)}
            />
          ) : (
            <CookiePanel
              cookie={cookie}
              connecting={connecting}
              error={cookieError}
              onCookieChange={(next) => {
                setCookie(next);
                if (cookieError) setCookieError(null);
              }}
              onConnect={() => void tryCookieConnect()}
            />
          )}
          <p className="text-2xs leading-relaxed text-tertiary text-center">
            <Lock
              aria-hidden
              className="inline-block align-text-bottom"
              style={{ width: 12, height: 12, marginRight: 4 }}
            />
            {t('step3.trust')}
          </p>
        </>
      )}
    </OnboardingStep>
  );
}

interface MethodTabsProps {
  method: ConnectMethod;
  onChange: (next: ConnectMethod) => void;
  extensionLabel: string;
  cookieLabel: string;
  recommendedLabel: string;
  comingSoonLabel: string;
}

function MethodTabs({
  method,
  onChange,
  extensionLabel,
  cookieLabel,
  recommendedLabel,
  comingSoonLabel,
}: MethodTabsProps) {
  return (
    <div
      role="tablist"
      aria-label={extensionLabel}
      className="flex items-center gap-1.5 rounded-md bg-bg-elevated p-1"
    >
      <button
        type="button"
        role="tab"
        aria-selected={method === 'extension'}
        data-testid="onboarding-method-extension"
        onClick={() => onChange('extension')}
        className={
          'flex flex-1 items-center justify-center gap-2 rounded px-3 py-2 text-xs font-semibold transition-colors ' +
          (method === 'extension'
            ? 'bg-bg-surface text-primary'
            : 'bg-transparent text-secondary hover:text-primary')
        }
      >
        <span>{extensionLabel}</span>
        <Pill tone="success" size="xs" variant="soft" uppercase>
          {recommendedLabel}
        </Pill>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={method === 'cookie'}
        aria-disabled
        disabled
        data-testid="onboarding-method-cookie"
        className={
          'flex flex-1 items-center justify-center gap-2 rounded px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ' +
          (method === 'cookie'
            ? 'bg-bg-surface text-primary'
            : 'bg-transparent text-secondary hover:text-primary')
        }
      >
        <span>{cookieLabel}</span>
        <Pill tone="neutral" size="xs" variant="soft" uppercase>
          {comingSoonLabel}
        </Pill>
      </button>
    </div>
  );
}

interface ExtensionPanelProps {
  presence: ReturnType<typeof useExtensionPresence>;
  onConnected: (account?: ConnectedAccountDisplay) => void;
  /** When true, mint a pairing code immediately on mount (used after the
   *  user clicks "Disconnect this device" so they don't have to click twice). */
  autoGenerate?: boolean;
  /** Callback fired after the auto-generate has been consumed so the parent
   *  can flip its `autoGenerate` flag back to false. */
  onAutoGenerated?: () => void;
}

interface PairingCodeState extends PairExtensionResponse {
  expiresAt: number;
}

type CopyState = 'idle' | 'copied' | 'failed';

function getConnectedAccountFromEvent(evt: UserHubServerEvent): ConnectedAccountDisplay | null {
  const payload = (evt as { payload?: unknown }).payload;
  if (!payload || typeof payload !== 'object') return null;
  const envelope = payload as Record<string, unknown>;
  if (envelope['source'] !== 'extension') return null;
  const account = envelope['account'];
  if (!account || typeof account !== 'object') return null;
  const raw = account as Record<string, unknown>;
  if (typeof raw['id'] !== 'string' || typeof raw['name'] !== 'string') return null;
  const profileUrl = typeof raw['profileUrl'] === 'string' ? raw['profileUrl'] : null;
  const vanitySlug = typeof raw['vanitySlug'] === 'string' ? raw['vanitySlug'] : null;
  const avatarUrl = typeof raw['avatarUrl'] === 'string' ? raw['avatarUrl'] : null;
  return {
    name: raw['name'],
    vanity: profileUrl ?? (vanitySlug ? `linkedin.com/in/${vanitySlug}` : ''),
    avatarUrl,
  };
}

function getConnectedAccountFromStatus(
  status: ExtensionStatusResponse | undefined,
): ConnectedAccountDisplay | null {
  if (!status?.accountConnected || !status.account) return null;
  if (!status.account.name) return null;
  return {
    name: status.account.name,
    vanity: status.account.profileUrl ?? '',
    avatarUrl: status.account.avatarUrl,
  };
}

function ExtensionPanel({
  presence,
  onConnected,
  autoGenerate,
  onAutoGenerated,
}: ExtensionPanelProps) {
  const t = useT('onboarding');
  const pairExtension = usePairExtension();
  const [pairing, setPairing] = useState<PairingCodeState | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const autoGenerateConsumedRef = useRef(false);
  const extensionStatus = useExtensionStatus({
    enabled: true,
    refetchInterval: 2000,
    retry: false,
  });
  const steps = [
    t('step3.extension.steps.install'),
    t('step3.extension.steps.openLinkedin'),
    t('step3.extension.steps.click'),
  ];
  const detected = presence === 'present';
  const extensionPaired = extensionStatus.data?.paired === true;
  const expiresIn = useMemo(() => {
    if (!pairing) return 0;
    return Math.max(0, Math.ceil((pairing.expiresAt - now) / 1000));
  }, [now, pairing]);
  const expired = pairing !== null && expiresIn === 0;
  const errorMessage =
    pairExtension.error instanceof ApiError
      ? pairExtension.error.message
      : pairExtension.error
        ? t('step3.extension.error')
        : null;

  useUserHubChannel('account.connected', (evt) => {
    const account = getConnectedAccountFromEvent(evt);
    if (!account) return;
    onConnected(account);
  });

  useEffect(() => {
    const account = getConnectedAccountFromStatus(extensionStatus.data);
    if (!account) return;
    onConnected(account);
  }, [extensionStatus.data, onConnected]);

  useEffect(() => {
    if (!pairing || expired) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [expired, pairing]);

  const generatePairingCode = useCallback(async () => {
    try {
      const response = await pairExtension.mutateAsync();
      setPairing({ ...response, expiresAt: Date.now() + response.expiresIn * 1000 });
      setNow(Date.now());
      setCopyState('idle');
    } catch {
      // React Query owns the error state rendered below.
    }
  }, [pairExtension]);

  useEffect(() => {
    if (!autoGenerate) return;
    if (autoGenerateConsumedRef.current) return;
    autoGenerateConsumedRef.current = true;
    void generatePairingCode().finally(() => onAutoGenerated?.());
  }, [autoGenerate, generatePairingCode, onAutoGenerated]);

  const copyCode = async () => {
    if (!pairing) return;
    try {
      await navigator.clipboard.writeText(pairing.code);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <Card padding="md" radius="lg" data-region="onboarding-extension">
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-dim text-accent"
          >
            <Puzzle style={{ width: 18, height: 18 }} />
          </span>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-bold text-primary">{t('step3.extension.title')}</span>
            <span className="text-xs leading-relaxed text-secondary">
              {t('step3.extension.body')}
            </span>
          </div>
        </div>
        <ol className="m-0 flex flex-col gap-2 border-t border-default pt-3 list-none p-0">
          {steps.map((label, idx) => (
            <li key={idx} className="flex items-center gap-2.5 text-xs text-secondary">
              <span
                aria-hidden
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-dim text-2xs font-bold text-accent"
              >
                {idx + 1}
              </span>
              <span>{label}</span>
            </li>
          ))}
        </ol>
        {detected ? (
          <Banner tone="success">{t('step3.extension.detected')}</Banner>
        ) : (
          <Banner tone="warning">{t('step3.extension.notDetected')}</Banner>
        )}
        {!HAS_PUBLISHED_EXTENSION && !detected ? (
          <Banner tone="warning">{t('step3.extension.unpublished')}</Banner>
        ) : null}
        {pairing ? (
          <div
            className="flex flex-col gap-2 rounded-md border border-default bg-bg-elevated p-3"
            data-testid="onboarding-extension-pairing-code"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-2xs font-semibold uppercase text-tertiary">
                {t('step3.extension.codeLabel')}
              </span>
              <span
                className={
                  'text-2xs font-semibold ' + (expired ? 'text-warning' : 'text-secondary')
                }
                role="status"
                aria-live="polite"
              >
                {expired
                  ? t('step3.extension.expired')
                  : t('step3.extension.expiresIn', { seconds: expiresIn })}
              </span>
            </div>
            <code className="select-all rounded bg-bg-surface px-3 py-2 text-center text-lg font-bold tracking-normal text-primary">
              {pairing.code}
            </code>
            <p className="text-xs leading-relaxed text-secondary">
              {t('step3.extension.pasteInstructions')}
            </p>
            <p className="text-2xs leading-relaxed text-tertiary">
              {t('step3.extension.manualFallback')}
            </p>
            {extensionPaired ? (
              <p className="text-2xs leading-relaxed text-tertiary" role="status">
                {t('step3.extension.pairedBody')}
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                fullWidth
                onClick={copyCode}
                disabled={!pairing}
                iconLeft={<Copy aria-hidden style={{ width: 14, height: 14 }} />}
                data-testid="onboarding-extension-copy-code"
              >
                {copyState === 'copied'
                  ? t('step3.extension.copySuccess')
                  : t('step3.extension.copyCta')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                fullWidth
                loading={pairExtension.isPending}
                onClick={generatePairingCode}
                iconLeft={<RefreshCw aria-hidden style={{ width: 14, height: 14 }} />}
                data-testid="onboarding-extension-regenerate"
              >
                {t('step3.extension.regenerateCta')}
              </Button>
            </div>
            {copyState === 'failed' ? (
              <p className="text-2xs leading-relaxed text-warning" role="status">
                {t('step3.extension.copyFailed')}
              </p>
            ) : null}
            <p className="text-2xs leading-relaxed text-tertiary">
              {t('step3.extension.pendingGap')}
            </p>
          </div>
        ) : null}
        {errorMessage ? <Banner tone="error">{errorMessage}</Banner> : null}
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          loading={pairExtension.isPending}
          onClick={generatePairingCode}
          data-testid="onboarding-extension-connect"
        >
          {pairExtension.isPending
            ? t('step3.extension.generating')
            : pairing
              ? t('step3.extension.generateNewCta')
              : t('step3.extension.connectCta')}
        </Button>
        {!detected && HAS_PUBLISHED_EXTENSION ? (
          <Button
            asChild
            variant="ghost"
            size="sm"
            fullWidth
            data-testid="onboarding-extension-install"
          >
            <a
              href={BAREL_EXTENSION_INSTALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2"
            >
              <span>{t('step3.extension.installCta')}</span>
              <ExternalLink aria-hidden style={{ width: 14, height: 14 }} />
            </a>
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

interface CookiePanelProps {
  cookie: string;
  connecting: boolean;
  /** Server-validation error rendered inline (US-022). `null` clears the banner. */
  error: string | null;
  onCookieChange: (next: string) => void;
  onConnect: () => void;
}

function CookiePanel({ cookie, connecting, error, onCookieChange, onConnect }: CookiePanelProps) {
  const t = useT('onboarding');
  const tooShort = cookie.trim().length > 0 && cookie.trim().length < COOKIE_MIN_LENGTH;
  const disabled = connecting || cookie.trim().length < COOKIE_MIN_LENGTH;
  return (
    <div className="flex flex-col gap-3">
      <Banner tone="warning">
        <strong>{t('step3.cookie.warningTitle')}</strong> {t('step3.cookie.warningBody')}
      </Banner>
      <Field invalid={tooShort || error != null}>
        <Field.Label>{t('step3.cookie.label')}</Field.Label>
        <Field.Control>
          <Input
            type="text"
            placeholder={t('step3.cookie.placeholder')}
            value={cookie}
            onChange={(event) => onCookieChange(event.target.value)}
            disabled={connecting}
            data-testid="onboarding-cookie-input"
            spellCheck={false}
            autoComplete="off"
          />
        </Field.Control>
        {tooShort ? <Field.Error>{t('step3.cookie.tooShort')}</Field.Error> : null}
      </Field>
      {error ? (
        <Banner tone="error" data-testid="onboarding-cookie-error">
          {error}
        </Banner>
      ) : null}
      <Button
        type="button"
        variant="primary"
        size="md"
        fullWidth
        disabled={disabled}
        loading={connecting}
        onClick={onConnect}
        data-testid="onboarding-cookie-connect"
      >
        {connecting ? t('step3.cookie.validating') : t('step3.cookie.cta')}
      </Button>
    </div>
  );
}

interface DetectedAccountCardProps {
  method: ConnectMethod;
  account?: ConnectedAccountDisplay | null;
  onDisconnect?: () => void;
  disconnectPending?: boolean;
  disconnectError?: string | null;
}

function DetectedAccountCard({
  method,
  account,
  onDisconnect,
  disconnectPending,
  disconnectError,
}: DetectedAccountCardProps) {
  const t = useT('onboarding');
  const display = account ?? {
    name: t('step3.connected.pendingName'),
    vanity: t('step3.connected.pendingVanity'),
    avatarUrl: null,
  };
  return (
    <Card padding="md" radius="xl" data-region="onboarding-detected">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Avatar
            size="lg"
            src={display.avatarUrl ?? undefined}
            initials={display.name
              .split(' ')
              .map((p) => p[0] ?? '')
              .join('')}
            alt={display.name}
          />
          <div className="flex flex-1 flex-col">
            <span className="text-sm font-bold text-primary">{display.name}</span>
            <span className="text-2xs text-secondary">{display.vanity}</span>
            <span className="text-xs font-semibold text-success" data-state="connected">
              {method === 'extension'
                ? t('step3.connected.viaExtension')
                : t('step3.connected.viaCookie')}
            </span>
          </div>
          <Spinner size="sm" tone="accent" aria-hidden style={{ visibility: 'hidden' }} />
        </div>
        {onDisconnect ? (
          <div className="flex flex-col gap-1 border-t border-default pt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={disconnectPending}
              onClick={() => {
                if (disconnectPending) return;
                onDisconnect();
              }}
              data-testid="onboarding-extension-disconnect"
            >
              {disconnectPending
                ? t('step3.connected.disconnectPending')
                : t('step3.connected.disconnectCta')}
            </Button>
            <p className="text-2xs leading-relaxed text-tertiary text-center">
              {t('step3.connected.disconnectHint')}
            </p>
            {disconnectError ? <Banner tone="error">{disconnectError}</Banner> : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
